import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  listJobDescriptions,
  type ApiJobDescriptionSummary,
} from '../../libs/api/jobDescriptionService';
import type { ApiPipelineStage, ApiTalentPoolReason } from '../../libs/api/pipelineService';
import {
  PIPELINE_STAGES,
  STAGE_LABELS,
  TALENT_POOL_REASON_LABELS,
  type PipelineCard,
} from './kanbanShared';

const TALENT_POOL_NOTE_MAX_LENGTH = 500;
const JD_PAGE_SIZE = 100; // API maximum

/** Loads every active JD, page by page, so the picker is not capped at one page. */
const listAllActiveJobDescriptions = async (): Promise<ApiJobDescriptionSummary[]> => {
  const firstPage = await listJobDescriptions({
    status: 'ACTIVE',
    page: 1,
    page_size: JD_PAGE_SIZE,
  });
  const pageCount = Math.ceil(firstPage.pagination.total / JD_PAGE_SIZE);
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) =>
      listJobDescriptions({ status: 'ACTIVE', page: index + 2, page_size: JD_PAGE_SIZE }),
    ),
  );

  return [firstPage, ...remainingPages].flatMap((response) => response.items);
};

interface MoveToTalentPoolModalProps {
  /** Also opened from the candidate detail (manager CV evaluation), so only what is shown. */
  card: Pick<PipelineCard, 'name' | 'role' | 'stage'> | null;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: ApiTalentPoolReason, note: string) => void;
}

export const MoveToTalentPoolModal: React.FC<MoveToTalentPoolModalProps> = ({
  card,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState<ApiTalentPoolReason | ''>('');
  const [note, setNote] = useState('');

  // The parent closes the modal once the move has been saved; start fresh for the next card.
  useEffect(() => {
    if (card) return;
    setReason('');
    setNote('');
  }, [card]);

  const canSubmit = reason !== '' && (reason !== 'OTHER' || note.trim().length > 0);

  const handleOpenChange = (open: boolean) => {
    if (open || submitting) return;
    onCancel();
  };

  return (
    <Dialog open={card !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-b border-border-subtle p-5">
          <DialogTitle className="flex items-center gap-2 text-text-main">
            <span className="size-2 rounded-full bg-violet-300" />
            Move to Talent Pool
          </DialogTitle>
          <DialogDescription className="text-text-subtle">
            {card ? `${card.name} · ${card.role} · from ${STAGE_LABELS[card.stage]}` : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 p-5">
          <div className="text-xs font-semibold text-text-main">
            Reason <span className="text-destructive">*</span>
          </div>
          <RadioGroup
            value={reason}
            onValueChange={(value) => setReason(value as ApiTalentPoolReason)}
            className="gap-2"
          >
            {(Object.keys(TALENT_POOL_REASON_LABELS) as ApiTalentPoolReason[]).map((value) => (
              <label
                key={value}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                  reason === value
                    ? 'border-primary/60 bg-primary-light/60 text-text-main'
                    : 'border-border-subtle bg-bg-main/40 text-text-main hover:border-primary/40'
                }`}
              >
                <RadioGroupItem value={value} />
                {TALENT_POOL_REASON_LABELS[value]}
              </label>
            ))}
          </RadioGroup>
          {reason === 'OTHER' ? (
            <Textarea
              value={note}
              onChange={(event) =>
                setNote(event.target.value.slice(0, TALENT_POOL_NOTE_MAX_LENGTH))
              }
              placeholder="Describe the reason..."
              className="mt-2 bg-bg-main"
              autoFocus
            />
          ) : null}
        </div>

        <DialogFooter className="border-t border-border-subtle p-4">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || submitting}
            onClick={() => {
              if (!reason) return;
              onConfirm(reason, reason === 'OTHER' ? note.trim() : '');
            }}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            Move to Talent Pool
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface MoveToJdModalProps {
  /** Card being moved out of the Talent Pool and the column it was dropped on. */
  pending: { card: PipelineCard; toStage: ApiPipelineStage } | null;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (jd: { id: string; title: string }, toStage: ApiPipelineStage) => void;
}

export const MoveToJdModal: React.FC<MoveToJdModalProps> = ({
  pending,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [jdId, setJdId] = useState('');
  const [toStage, setToStage] = useState<ApiPipelineStage | ''>('');

  const { data: activeJds = [], isLoading } = useQuery({
    queryKey: ['job-descriptions', { status: 'ACTIVE', all: true }],
    queryFn: listAllActiveJobDescriptions,
    enabled: pending !== null,
  });

  const selectedStage = toStage || pending?.toStage || '';

  // The parent closes the modal once the transfer has been saved; start fresh for the next card.
  useEffect(() => {
    if (pending) return;
    setJdId('');
    setToStage('');
  }, [pending]);

  const handleOpenChange = (open: boolean) => {
    if (open || submitting) return;
    onCancel();
  };

  return (
    <Dialog open={pending !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-b border-border-subtle p-5">
          <DialogTitle className="text-text-main">Move to another JD</DialogTitle>
          <DialogDescription className="text-text-subtle">
            {pending ? `${pending.card.name} · previously ${pending.card.role}` : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-5">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-text-main">
              Target JD <span className="text-destructive">*</span>
            </label>
            <Select value={jdId} onValueChange={setJdId} disabled={isLoading}>
              <SelectTrigger className="w-full bg-bg-main">
                <SelectValue placeholder={isLoading ? 'Loading JDs...' : 'Select an active JD'} />
              </SelectTrigger>
              <SelectContent>
                {activeJds.map((jd) => (
                  <SelectItem key={jd.id} value={jd.id}>
                    {jd.department ? `${jd.title} · ${jd.department}` : jd.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoading && activeJds.length === 0 ? (
              <p className="text-xs text-text-subtle">There are no active JDs.</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-text-main">
              Start the new pipeline at
            </label>
            <Select
              value={selectedStage}
              onValueChange={(value) => setToStage(value as ApiPipelineStage)}
            >
              <SelectTrigger className="w-full bg-bg-main">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PIPELINE_STAGES.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {STAGE_LABELS[stage]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="border-t border-border-subtle p-4">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!jdId || !selectedStage || submitting}
            onClick={() => {
              const jd = activeJds.find((item) => item.id === jdId);
              if (!jd || !selectedStage) return;
              onConfirm({ id: jd.id, title: jd.title }, selectedStage);
            }}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            Move candidate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
